// src/services/favoritesService.ts

import { Cookies } from "react-cookie";
import type { FavoriteItem } from "../contexts/FavoritesContext";
import type { ApiShirt } from "../types/Shirt";

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:8080";

const cookies = new Cookies();

function getAccessToken(): string | null {
  const token = cookies.get("access_token");
  if (typeof token !== "string") return null;
  const t = token.trim();
  return t || null;
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function mapLikedShirtsToFavorites(shirts: ApiShirt[]): FavoriteItem[] {
  return shirts.map((shirt) => ({
    id: shirt.id,
    isAuction: shirt.sales_type === "auction",
    likedAt: Date.now(),
  }));
}

async function apiGetLikedShirts(): Promise<ApiShirt[]> {
  const res = await fetch(`${API_BASE}/shirts/my/liked`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });

  if (!res.ok) throw new Error("Gelikete shirts ophalen mislukt");
  return (await res.json()) as ApiShirt[];
}

async function apiGetFavorites(): Promise<FavoriteItem[]> {
  const shirts = await apiGetLikedShirts();
  return mapLikedShirtsToFavorites(shirts);
}

async function apiAddFavorite(
  id: string,
  isAuction: boolean,
): Promise<FavoriteItem> {
  const res = await fetch(`${API_BASE}/shirts/${id}/like`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
  });

  if (!res.ok) throw new Error("Favoriet toevoegen mislukt");
  return { id, isAuction, likedAt: Date.now() };
}

async function apiRemoveFavorite(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/shirts/${id}/like`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });

  if (!res.ok) throw new Error("Favoriet verwijderen mislukt");
}

/* ----------------------------- PUBLIC SERVICE ----------------------------- */

export const favoritesService = {
  async getLikedShirts(): Promise<ApiShirt[]> {
    return apiGetLikedShirts();
  },

  async getFavorites(): Promise<FavoriteItem[]> {
    return apiGetFavorites();
  },

  async addFavorite(id: string, isAuction: boolean): Promise<FavoriteItem> {
    return apiAddFavorite(id, isAuction);
  },

  async removeFavorite(id: string): Promise<void> {
    return apiRemoveFavorite(id);
  },
};
